import { useState } from 'react';
import { Link } from 'react-router-dom';
import { History, UserRound, UserSearch } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/Sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import type { AuditLog } from '@/features/users/hooks';
import {
  ACTION_VARIANT,
  aalLabel,
  actionLabel,
  actorDisplayName,
  actorEmail,
  actorInitials,
  diffAuditRow,
  entityDisplayName,
  entityTypeLabel,
  formatFieldValue,
  isSystemActor,
  redactedJson,
  summarizeUserAgent,
  type AuditActorProfile,
} from '@/features/users/auditLog';
import { formatDateTime, formatRelative } from '@/lib/formatters';
import { ROLE_LABELS, type Role } from '@/lib/permissions';
import { cn } from '@/lib/utils';

interface AuditEventSheetProps {
  row: AuditLog | null;
  actor: AuditActorProfile | undefined;
  onClose: () => void;
  /** Pivot the explorer to every event by this actor. */
  onFilterActor?: (actorId: string) => void;
  /** Pivot the explorer to the full history of this record. */
  onFilterEntity?: (entityType: string, entityId: string) => void;
}

export function AuditEventSheet({
  row,
  actor,
  onClose,
  onFilterActor,
  onFilterEntity,
}: AuditEventSheetProps) {
  const [showUnchanged, setShowUnchanged] = useState(false);
  const [showRaw, setShowRaw] = useState(false);

  return (
    <Sheet open={row != null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="max-w-2xl">
        {row && (
          <>
            <SheetHeader>
              <div className="flex items-center gap-2 pr-8">
                <Badge variant={ACTION_VARIANT[row.action] ?? 'muted'}>
                  {actionLabel(row.action)}
                </Badge>
                <SheetTitle>{entityTypeLabel(row.entity_type)}</SheetTitle>
              </div>
              <SheetDescription>
                {formatDateTime(row.created_at, { seconds: true })} ·{' '}
                {formatRelative(row.created_at)}
              </SheetDescription>
            </SheetHeader>
            <SheetBody className="space-y-6">
              <div className="flex flex-wrap gap-2">
                {row.actor_id && onFilterActor && (
                  <Button variant="outline" size="sm" onClick={() => onFilterActor(row.actor_id!)}>
                    <UserSearch className="h-3.5 w-3.5" />
                    All activity by this user
                  </Button>
                )}
                {row.entity_id && onFilterEntity && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onFilterEntity(row.entity_type, row.entity_id!)}
                  >
                    <History className="h-3.5 w-3.5" />
                    History of this record
                  </Button>
                )}
                {row.actor_id && actor && (
                  <Button asChild variant="outline" size="sm">
                    <Link to={`/hr/employees/${row.actor_id}?tab=activity`}>
                      <UserRound className="h-3.5 w-3.5" />
                      Open employee profile
                    </Link>
                  </Button>
                )}
              </div>
              <ActorSection row={row} actor={actor} />
              <EventDetails row={row} />
              <ChangesSection
                row={row}
                showUnchanged={showUnchanged}
                onToggleUnchanged={() => setShowUnchanged((v) => !v)}
              />
              <section className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-foreground">Raw snapshots</h3>
                  <Button variant="ghost" size="sm" onClick={() => setShowRaw((v) => !v)}>
                    {showRaw ? 'Hide' : 'Show'}
                  </Button>
                </div>
                {showRaw && (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <RawJson label="Before" value={row.before} />
                    <RawJson label="After" value={row.after} />
                  </div>
                )}
              </section>
            </SheetBody>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </>
  );
}

function ActorSection({ row, actor }: { row: AuditLog; actor: AuditActorProfile | undefined }) {
  const email = actorEmail(row, actor);
  const roleLabel = row.actor_role ? (ROLE_LABELS[row.actor_role as Role] ?? row.actor_role) : null;
  const aal = aalLabel(row.auth_aal);
  const uaSummary = summarizeUserAgent(row.user_agent);
  const system = isSystemActor(row);
  const profileGone = !system && !actor;

  return (
    <section>
      <h3 className="mb-2 text-sm font-semibold text-foreground">Who</h3>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
        <Row label="User">
          <div className="flex items-center gap-2.5">
            <Avatar className="h-7 w-7">
              {actor?.avatar_url && <AvatarImage src={actor.avatar_url} alt="" />}
              <AvatarFallback className="text-[10px]">{actorInitials(row, actor)}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="text-foreground">
                {actorDisplayName(row, actor)}
                {roleLabel && (
                  <Badge variant="info-soft" className="ml-2 align-middle">
                    {roleLabel}
                  </Badge>
                )}
              </p>
              {email && <p className="text-xs text-muted-foreground">{email}</p>}
              {system && (
                <p className="text-xs text-muted-foreground">
                  Automated change (sync, integration, or server job)
                </p>
              )}
              {profileGone && (
                <p className="text-xs text-fresh-salmon-dark">
                  This account no longer exists in Atlas; identity shown as recorded at the time.
                </p>
              )}
            </div>
          </div>
        </Row>
        <Row label="Sign-in">
          {aal ? (
            <Badge variant={row.auth_aal === 'aal2' ? 'success-soft' : 'warning-soft'}>{aal}</Badge>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </Row>
        <Row label="IP address">
          <span className="font-mono text-xs text-foreground">{row.ip_address ?? '—'}</span>
        </Row>
        <Row label="Device">
          {row.user_agent ? (
            <>
              <p className="text-foreground">{uaSummary}</p>
              <p className="break-all font-mono text-[11px] text-muted-foreground">
                {row.user_agent}
              </p>
            </>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </Row>
        <Row label="Session">
          <span className="font-mono text-xs text-foreground">{row.session_id ?? '—'}</span>
        </Row>
        <Row label="Actor id">
          <span className="font-mono text-xs text-foreground">{row.actor_id ?? '—'}</span>
        </Row>
      </dl>
    </section>
  );
}

function EventDetails({ row }: { row: AuditLog }) {
  const when = new Date(row.created_at);
  const entityName = entityDisplayName(row);
  return (
    <section>
      <h3 className="mb-2 text-sm font-semibold text-foreground">What</h3>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
        <Row label="When">
          <p className="text-foreground">{formatDateTime(when, { seconds: true })}</p>
          <p className="font-mono text-xs text-muted-foreground">{when.toISOString()} (UTC)</p>
        </Row>
        <Row label="Change">
          <p className="text-foreground">
            {actionLabel(row.action)} {entityTypeLabel(row.entity_type).toLowerCase()}
            {entityName && (
              <>
                {' '}
                <span className="font-medium">“{entityName}”</span>
              </>
            )}
          </p>
        </Row>
        <Row label="Entity id">
          <span className="font-mono text-xs text-foreground">{row.entity_id ?? '—'}</span>
        </Row>
        <Row label="Event id">
          <span className="font-mono text-xs text-foreground">{row.id}</span>
        </Row>
      </dl>
    </section>
  );
}

function ChangesSection({
  row,
  showUnchanged,
  onToggleUnchanged,
}: {
  row: AuditLog;
  showUnchanged: boolean;
  onToggleUnchanged: () => void;
}) {
  const all = diffAuditRow(row);
  const changed = all.filter((c) => c.changed);
  const isUpdate = row.action === 'update';
  const visible = isUpdate && !showUnchanged ? changed : all;
  const hasBefore = row.before != null;
  const hasAfter = row.after != null;

  if (all.length === 0) {
    return (
      <section>
        <h3 className="mb-2 text-sm font-semibold text-foreground">Changes</h3>
        <p className="text-sm text-muted-foreground">No snapshot was recorded for this event.</p>
      </section>
    );
  }

  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">
          {isUpdate
            ? `${changed.length} field${changed.length === 1 ? '' : 's'} changed`
            : row.action === 'insert'
              ? 'Values recorded'
              : row.action === 'delete'
                ? 'Values removed'
                : 'Details'}
        </h3>
        {isUpdate && all.length > changed.length && (
          <Button variant="ghost" size="sm" onClick={onToggleUnchanged}>
            {showUnchanged ? 'Hide unchanged' : `Show all ${all.length} fields`}
          </Button>
        )}
      </div>
      {visible.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Only <code className="font-mono text-xs">updated_at</code> moved on this write.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Field</TableHead>
              {hasBefore && <TableHead>Before</TableHead>}
              {hasAfter && <TableHead>After</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.map((c) => (
              <TableRow key={c.field} className={cn(!c.changed && 'text-muted-foreground')}>
                <TableCell className="whitespace-nowrap font-mono text-xs">{c.field}</TableCell>
                {hasBefore && (
                  <TableCell
                    className={cn(
                      'font-mono text-xs',
                      c.changed &&
                        isUpdate &&
                        'text-fresh-salmon-dark line-through decoration-fresh-salmon-dark/50',
                    )}
                  >
                    {formatFieldValue(c.before, { sensitive: c.sensitive })}
                  </TableCell>
                )}
                {hasAfter && (
                  <TableCell
                    className={cn(
                      'font-mono text-xs',
                      c.changed && isUpdate && 'text-bright-moss-dark',
                    )}
                  >
                    {formatFieldValue(c.after, { sensitive: c.sensitive })}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </section>
  );
}

function RawJson({ label, value }: { label: string; value: unknown }) {
  return (
    <div className="min-w-0">
      <p className="mb-1 text-xs font-medium text-muted-foreground">{label}</p>
      <pre className="max-h-80 overflow-auto rounded-lg border border-border bg-secondary/40 p-3 font-mono text-[11px] leading-relaxed text-foreground">
        {value == null ? '—' : redactedJson(value)}
      </pre>
    </div>
  );
}
